import React, { Fragment, useEffect, useState } from 'react';
import {
    Button,
    FormControl,
    FormHelperText,
    Box,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Theme,
    makeStyles,
    Avatar as MuiAvatar,
    Typography,
    lighten,
    useTheme,
} from '@material-ui/core';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Save } from 'react-feather';
import useStore from 'src/hooks/useStore';
import useAuth from 'src/hooks/useAuth';
import { CapitalizeNames } from 'src/utils/capitalize';
import { IStore } from 'src/interfaces/store';
import { useContact } from 'src/hooks/useContact';
import { ErrorAlert } from 'src/components/Lead/Duplicated';
import {
    IContactFormProps,
    TContactFormValues,
    defaultContactFormValues,
} from './types';
import { AreaCodeTextField } from 'src/modules/forms/fields/AreaCodeTextField/AreaCodeTextField';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { USER_LEVEL_ACCESS } from 'src/components/access';
import { ContactFormUsersField } from './ContactFormUsersField';
import {
    CONTACT_DEFAULT_AVATAR,
    TContactAvatar,
} from 'src/components/contacts/constants/images';
import { getDicebearAvatar } from 'src/utils/images/getDicebearAvatar';
import { getContactVariants } from 'src/components/contacts/constants/images';
import { AvatarPopover } from '../AvatarPopover';
import { Skeleton } from '@material-ui/lab';
import moment from 'moment';
import { collectUniqueStoreTags } from 'src/views/OmniChannel/utils/storeTags';
import { SelectableTags } from 'src/components/Tags/components';
import { IStoreTag } from 'src/constants/store/tags';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    CONTACT_AUTH,
    CREATE_PERMISSION,
    UPDATE_PERMISSION,
} from 'src/constants/permissions';

const useStyles = makeStyles((theme: Theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    submitButton: {
        height: 56,
    },
}));

const optionalPhoneValidation = (t: (key: string) => string) =>
    Yup.string()
        .matches(/^[0-9]*$/, t('Yup.Number'))
        .test(
            'len',
            t('Yup.Phone'),
            (val: string | undefined) =>
                !val || val.length === 0 || val.length === 10
        );

export const ContactForm = ({
    mode,
    initialValues = defaultContactFormValues,
    onSubmit,
    isPhoneRequired = false,
    loading = false,
    createdBy,
    createdAt,
}: IContactFormProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { error } = useContact();
    const { stores, getStores } = useStore();
    const [currentAvatar, setCurrentAvatar] = useState<TContactAvatar>(
        CONTACT_DEFAULT_AVATAR
    );
    const [avatarAnchorEl, setAvatarAnchorEl] = useState<HTMLElement | null>(
        null
    );
    const [contactTags, setContactTags] = useState([]);

    const isEditMode = mode === 'edit';

    const canEditContact = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: CONTACT_AUTH,
        crud: UPDATE_PERMISSION,
    });

    const canCreateContact = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: CONTACT_AUTH,
        crud: CREATE_PERMISSION,
    });

    useEffect(() => {
        if (!user?._id) return;
        getStores();
    }, [user]);

    const phoneValidation = isPhoneRequired
        ? optionalPhoneValidation(t).required(t('Yup.Required'))
        : optionalPhoneValidation(t);

    const validationSchema = Yup.object({
        name: Yup.string()
            .required(t('Yup.Required'))
            .max(80, t('Yup.MaxLength')),
        email: Yup.string().email(t('Yup.Email')),
        phone: phoneValidation,
        secondaryPhone: optionalPhoneValidation(t),
        employment: Yup.string().max(120, t('Yup.MaxLength')),
        store: Yup.string().required(t('Yup.Required')),
    });

    const initialFormValues: TContactFormValues = {
        ...defaultContactFormValues,
        ...initialValues,
        store:
            getDocumentId(initialValues.store) ??
            defaultContactFormValues.store,
        countryCode:
            initialValues.countryCode ?? defaultContactFormValues.countryCode,
        secondaryCountryCode:
            initialValues.secondaryCountryCode ??
            defaultContactFormValues.secondaryCountryCode,
        users: initialValues.users ?? defaultContactFormValues.users,
    };

    const showUsersField =
        isEditMode && canEditContact && user?.accessLevel !== USER_LEVEL_ACCESS;

    const currentAvatarUri = getDicebearAvatar({
        style: currentAvatar.style,
        seed: currentAvatar.seed,
        variants: getContactVariants(lighten(theme.palette.primary.main, 0.8)),
    });

    useEffect(() => {
        if (initialValues.image) setCurrentAvatar({ ...initialValues.image });
    }, [initialValues]);

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAvatarAnchorEl(event.currentTarget);
    };

    const handleCloseAvatarPicker = () => {
        setAvatarAnchorEl(null);
    };

    const isAvatarPickerOpen = Boolean(avatarAnchorEl);

    const handleSelectAvatar = (response: TContactAvatar) =>
        setCurrentAvatar(response);

    const [storeTags, setStoreTags] = useState([]);

    useEffect(() => {
        const currentStore = stores.find(
            (item) => item._id === initialValues.store
        );

        const response = collectUniqueStoreTags([currentStore]);

        setStoreTags(response);
    }, [initialValues.store]);

    const handleSelectTag = (tag: IStoreTag) => {
        setContactTags((prev) => {
            const exists = prev.some(
                (currentTag) => currentTag.name === tag.name
            );

            if (exists) {
                return prev.filter(
                    (currentTag) => currentTag.name !== tag.name
                );
            }

            return [...prev, tag];
        });
    };

    useEffect(() => {
        setContactTags(initialValues.tags);
    }, [initialValues.tags]);

    const showCreateButton = mode === 'create' && canCreateContact && !loading;
    const showUpdateButton = mode == 'edit' && canEditContact && !loading;

    const renderSubmitButton = (disabled: boolean, mode: 'create' | 'edit') => (
        <Button
            data-testid="contact-form-submit"
            type="submit"
            color="primary"
            variant="contained"
            startIcon={<Save />}
            disabled={disabled}
            className={classes.submitButton}
            fullWidth
        >
            {mode === 'create' && String(t('contacts.Create'))}
            {mode === 'edit' && String(t('Buttons.Save'))}
        </Button>
    );

    return (
        <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values) => {
                onSubmit({
                    ...values,
                    image: currentAvatar,
                    tags: contactTags,
                });
            }}
        >
            {(formikProps: FormikProps<TContactFormValues>) => {
                const {
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                } = formikProps;

                return (
                    <Form data-testid="contact-form">
                        <Box className={classes.formContainer}>
                            {loading ? (
                                <Skeleton
                                    height={120}
                                    width={120}
                                    variant="circle"
                                    style={{
                                        alignSelf: 'center',
                                    }}
                                />
                            ) : (
                                <MuiAvatar
                                    src={currentAvatarUri}
                                    alt={currentAvatar?.seed}
                                    onClick={handleAvatarClick}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        cursor: 'pointer',
                                        alignSelf: 'center',
                                    }}
                                />
                            )}
                            <AvatarPopover
                                open={isAvatarPickerOpen}
                                onClose={handleCloseAvatarPicker}
                                anchorEl={avatarAnchorEl}
                                onSelect={handleSelectAvatar}
                            />

                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 20,
                                }}
                            >
                                {loading ? (
                                    Array.from({ length: 7 }).map(
                                        (_, index) => (
                                            <Skeleton
                                                key={index}
                                                height={60}
                                                width="100%"
                                                variant="rect"
                                                style={{
                                                    borderRadius: 5,
                                                }}
                                            />
                                        )
                                    )
                                ) : (
                                    <Fragment>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={String(t('contacts.name'))}
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={!canEditContact}
                                            error={
                                                touched.name &&
                                                Boolean(errors.name)
                                            }
                                            helperText={
                                                touched.name && errors.name
                                            }
                                            inputProps={{
                                                'data-testid':
                                                    'contact-form-name',
                                            }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={String(t('contacts.email'))}
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={!canEditContact}
                                            error={
                                                touched.email &&
                                                Boolean(errors.email)
                                            }
                                            helperText={
                                                touched.email && errors.email
                                            }
                                            inputProps={{
                                                'data-testid':
                                                    'contact-form-email',
                                            }}
                                        />
                                        <AreaCodeTextField
                                            name="phone"
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={handleChange}
                                            props={{
                                                disabled: !canEditContact,
                                                variant: 'outlined',
                                                fullWidth: true,
                                                label: String(
                                                    t('contacts.phone')
                                                ),
                                                required: isPhoneRequired,
                                            }}
                                        />
                                        <AreaCodeTextField
                                            name="secondaryPhone"
                                            countryCodeName="secondaryCountryCode"
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={handleChange}
                                            props={{
                                                disabled: !canEditContact,
                                                variant: 'outlined',
                                                fullWidth: true,
                                                label: String(
                                                    t('contacts.secondaryPhone')
                                                ),
                                                required: isPhoneRequired,
                                            }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={String(
                                                t('contacts.employment')
                                            )}
                                            name="employment"
                                            value={values.employment}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={!canEditContact}
                                            error={
                                                touched.employment &&
                                                Boolean(errors.employment)
                                            }
                                            helperText={
                                                touched.employment &&
                                                errors.employment
                                            }
                                            inputProps={{
                                                'data-testid':
                                                    'contact-form-employment',
                                            }}
                                        />
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={
                                                touched.store &&
                                                Boolean(errors.store)
                                            }
                                        >
                                            <InputLabel>
                                                {String(t('contacts.agency'))}
                                            </InputLabel>
                                            <Select
                                                name="store"
                                                value={values.store}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={!canEditContact}
                                                label={String(
                                                    t('contacts.agency')
                                                )}
                                            >
                                                {stores.map((store: IStore) => (
                                                    <MenuItem
                                                        key={store._id}
                                                        value={store._id}
                                                    >
                                                        {CapitalizeNames(
                                                            store.name
                                                        )}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.store && errors.store}
                                            </FormHelperText>
                                        </FormControl>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={String(t('contacts.notes'))}
                                            name="notes"
                                            value={values.notes}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={!canEditContact}
                                            multiline
                                            minRows={3}
                                            maxRows={8}
                                            inputProps={{
                                                'data-testid':
                                                    'contact-form-notes',
                                            }}
                                        />

                                        {storeTags?.length > 0 && (
                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 15,
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    color="textPrimary"
                                                >
                                                    {t('Stores.Tags.Title')}:
                                                </Typography>
                                                <SelectableTags
                                                    tags={storeTags}
                                                    onSelect={
                                                        canEditContact
                                                            ? handleSelectTag
                                                            : null
                                                    }
                                                    currentTags={contactTags}
                                                />
                                            </Box>
                                        )}

                                        {showUsersField && (
                                            <ContactFormUsersField
                                                storeId={values.store}
                                                value={values.users}
                                                onChange={(selected) =>
                                                    setFieldValue(
                                                        'users',
                                                        selected
                                                    )
                                                }
                                            />
                                        )}
                                        {createdBy && (
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    color="textPrimary"
                                                >
                                                    {t('contacts.createdBy')}:
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="textPrimary"
                                                >
                                                    {createdBy}
                                                </Typography>
                                            </Box>
                                        )}
                                        {createdAt && (
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    color="textPrimary"
                                                >
                                                    {t('contacts.createdAt')}:
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="textPrimary"
                                                >
                                                    {moment(createdAt).format(
                                                        'lll'
                                                    )}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Fragment>
                                )}
                            </Box>

                            {error && (
                                <Box mt={2}>
                                    <ErrorAlert error={error} />
                                </Box>
                            )}

                            {showCreateButton &&
                                renderSubmitButton(!showCreateButton, mode)}
                            {showUpdateButton &&
                                renderSubmitButton(!showUpdateButton, mode)}
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};
